import axios from 'axios';
import qs from 'qs';

import { store } from '@/store';
import router from '@/router';


const apiUrl = process.env.VUE_APP_API_URL;

const ax = axios.create({
    baseURL: apiUrl,
    timeout: 15000,
});

// Add token on every request
ax.interceptors.request.use(config => {
    if (config.url !== '/v1/auth/token/' &&
        config.baseURL === apiUrl &&
        !config.headers.Authorization) {
        const token = store.getAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
    }
    return config;
});

// Redirect to login on 401
ax.interceptors.response.use(response => {
    return response;
}, (error) => {
    if (error.response && error.response.status == 401) {
        router.push({name: 'login'});
        store.setAuth(null);
    }
    return Promise.reject(error);
});

export default {
    apiUrl: apiUrl,
    axios: ax,

    getError (error) {
        if (error.response && error.response.data) {
            error.response.data.status = error.response.status;
            error.response.data.type = 'api';
            return error.response.data;
        }
        return {
            type: 'unknown',
            error_description: error.message,
            status: error.response ? error.response.status : null,
            error: 'api_error',
        };
    },

    async refreshToken () {
        try {
            const token = store.getRefreshToken();
            if (!token) {
                throw 'Refresh token is missing';
            }
            const response = await ax.post('/v1/auth/token/', qs.stringify({
                grant_type: 'refresh_token',
                refresh_token: token
            }));
            return response.data;
        } catch (error) {
            console.error('Error on token refresh');
            console.error(error);
        }
    },

    // Refresh token and set auth in one action
    async doRefresh () {
        const result = await this.refreshToken();
        if (result) {
            store.setAuth(result);
        } else {
            store.setAuth(null);
            throw 'Refresh failed';
        }
    },

    async login (username, password) {
        try {
            const response = await ax.post('/v1/auth/token/', qs.stringify({
                grant_type: 'password',
                username: username,
                password: password
            }));
            return response.data;
        } catch (error) {
            throw this.getError(error);
        }
    },

    async logout () {
        try {
            const response = await ax.post('/v1/auth/logout/', {
                access_token: store.state.access_token,
                refresh_token: store.state.refresh_token
            });
            return response.data;
        } catch (error) {
            throw this.getError(error);
        }
    },

    // Users

    async getUser (id) {
        try {
            const response = await ax.get(`/v1/users/${id}/`);
            return response.data.user;
        } catch (error) {
            throw this.getError(error);
        }
    },

    // Projects

    async getProjects () {
        try {
            const response = await ax.get('/v1/projects/');
            return response.data.projects;
        } catch (error) {
            throw this.getError(error);
        }
    },

    async generateKey (projectId) {
        try {
            const response = await ax.post(
                `/v1/projects/${projectId}/keys/`
            );
            return response.data.api_keys;
        } catch (error) {
            throw this.getError(error);
        }
    },

    async deleteKey (projectId, index) {
        try {
            const response = await ax.delete(
                `/v1/projects/${projectId}/keys/${index}/`
            );
            return response.data.api_keys;
        } catch (error) {
            throw this.getError(error);
        }
    },

    // Phones

    async getPhones (projectId) {
        try {
            const response = await ax.get(`/v1/projects/${projectId}/phones/`);
            return response.data.phones;
        } catch (error) {
            throw this.getError(error);
        }
    },

    async getPhone (projectId, phoneId) {
        try {
            const response = await ax.get(`/v1/projects/${projectId}/phones/${phoneId}/`);
            return response.data.phone;
        } catch (error) {
            throw this.getError(error);
        }
    },

    async createPhone (projectId, data) {
        try {
            const response = await ax.post(
                `/v1/projects/${projectId}/phones/`, data
            );
            return response.data.phone;
        } catch (error) {
            throw this.getError(error);
        }
    },

    async updatePhone (projectId, phoneId, data) {
        try {
            const response = await ax.put(
                `/v1/projects/${projectId}/phones/${phoneId}/`, data
            );
            return response.data.phone;
        } catch (error) {
            throw this.getError(error);
        }
    },

    // Statistics
    async getShortStats (projectId) {
        try {
            const response = await ax.get(
                `/v1/projects/${projectId}/statistics/short/`
            );
            return response.data.data;
        } catch (error) {
            throw this.getError(error);
        }
    },

    async getStatisticsIncoming (projectId) {
        try {
            const response = await ax.get(
                `/v1/projects/${projectId}/statistics/incoming/`
            );
            return response.data;
        } catch (error) {
            throw this.getError(error);
        }
    },

    async getStatisticsOutgoing (projectId) {
        try {
            const response = await ax.get(
                `/v1/projects/${projectId}/statistics/outgoing/`
            );
            return response.data;
        } catch (error) {
            throw this.getError(error);
        }
    },

    async getStatisticsTime (projectId, filter) {
        try {
            const response = await ax.get(
                `/v1/projects/${projectId}/statistics/time/`, {params: filter}
            );
            return response.data.stats;
        } catch (error) {
            throw this.getError(error);
        }
    },

    // Calls
    async getCalls (projectId, filter) {
        try {
            const response = await ax.get(
                `/v1/projects/${projectId}/calls/`, {params: filter}
            );
            return response.data;
        } catch (error) {
            throw this.getError(error);
        }
    },

}
